import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../contexts/HospitalOnboardingContext';
import DynamicContactsTable from '../../components/hospitalOnboarding/DynamicContactsTable';
import {
  Mic,
  Activity,
  Bed,
  ArrowLeft,
  ArrowRight,
  Bookmark,
  Check
} from 'lucide-react';

const EMERGENCY_FACILITIES = [
  'Emergency Department',
  'Trauma Center',
  'ICU',
  'NICU / PICU',
  'Cath Lab'
];

export const Step2FacilitiesContacts = () => {
  const navigate = useNavigate();
  const { formData, updateFields, saveAndContinue, goBack, saveAndExit, isSaving } = useHospitalOnboarding();
  const [errors, setErrors] = useState({});

  const handleFacilityToggle = (facility) => {
    const currentFacilities = formData.emergency_facilities || [];
    const updated = currentFacilities.includes(facility)
      ? currentFacilities.filter((f) => f !== facility)
      : [...currentFacilities, facility];
    updateFields({ emergency_facilities: updated });
  };

  const handleContinue = (e) => {
    e.preventDefault();
    setErrors({});

    const contacts = formData.contacts || [];
    let hasPhoneError = false;

    for (let i = 0; i < contacts.length; i++) {
      const c = contacts[i];
      const trimmedMobile = (c.mobile || '').trim();
      const hasContent = Boolean(c.name?.trim() || c.role?.trim() || trimmedMobile);

      if (hasContent) {
        if (!trimmedMobile) {
          setErrors({ contacts: `Contact #${i + 1} requires a 10-digit mobile number.` });
          hasPhoneError = true;
          break;
        } else if (!/^\d{10}$/.test(trimmedMobile)) {
          setErrors({ contacts: `Contact #${i + 1} mobile number must be exactly 10 digits.` });
          hasPhoneError = true;
          break;
        }
      }
    }

    if (hasPhoneError) return;

    saveAndContinue();
  };

  return (
    <div className="w-full">
      {/* Header Row: Step info and Switch to Voice */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 block mb-1">
            STEP 2 OF 5
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight leading-tight">
            Facilities & contacts
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1 font-normal">
            Help us connect with the right emergency medical and administrative leaders.
          </p>
          {/* Smart Ambulance Blue accent line */}
          <div className="w-14 sm:w-16 h-0.5 bg-blue-600 mt-2 rounded-full" />
        </div>

        <button
          type="button"
          onClick={() => navigate('/hospital/onboarding/voice?type=hospital&step=2')}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-xs font-medium text-slate-700 shadow-2xs transition-all cursor-pointer self-start sm:self-auto"
        >
          <Mic className="w-3.5 h-3.5 text-slate-600" />
          <span>Switch to voice</span>
        </button>
      </div>

      <form onSubmit={handleContinue} className="space-y-6">
        {/* Card 1: Emergency Capability */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-xs p-6 sm:p-8">
          <div className="flex items-center gap-3.5 mb-6 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
              <Activity className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-800 leading-tight">
                Emergency capability
              </h3>
              <p className="text-xs text-slate-500 mt-0.5 font-normal">
                Emergency departments, trauma units and intensive care setups.
              </p>
            </div>
          </div>

          {/* Facilities Multi-Checkbox Pills */}
          <div className="mb-6">
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Facilities
            </label>
            <div className="flex flex-wrap gap-2 sm:gap-2.5">
              {EMERGENCY_FACILITIES.map((facility) => {
                const isSelected = (formData.emergency_facilities || []).includes(facility);
                return (
                  <button
                    key={facility}
                    type="button"
                    onClick={() => handleFacilityToggle(facility)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs transition-all select-none cursor-pointer ${
                      isSelected
                        ? 'bg-blue-50 border-blue-400 text-blue-700 font-medium shadow-2xs'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 font-normal'
                    }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                        isSelected
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-slate-300 bg-white'
                      }`}
                    >
                      {isSelected && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                    </span>
                    <span>{facility}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ICU Beds & Emergency Beds */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                ICU Beds
              </label>
              <div className="relative">
                <Bed className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="number"
                  min="0"
                  placeholder="Enter ICU beds"
                  value={formData.icu_beds || ''}
                  onChange={(e) => updateFields({ icu_beds: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Emergency Beds
              </label>
              <div className="relative">
                <Bed className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="number"
                  min="0"
                  placeholder="Enter emergency beds"
                  value={formData.emergency_beds || ''}
                  onChange={(e) => updateFields({ emergency_beds: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Total Beds */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Total Beds
              </label>
              <div className="relative">
                <Bed className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="number"
                  min="0"
                  placeholder="Enter total beds"
                  value={formData.total_beds || formData.number_of_beds || ''}
                  onChange={(e) => updateFields({ total_beds: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Key Contacts */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-xs p-6 sm:p-8">
          <DynamicContactsTable
            contacts={formData.contacts || []}
            onChange={(updatedContacts) => {
              updateFields({ contacts: updatedContacts });
              if (errors.contacts) setErrors((prev) => ({ ...prev, contacts: '' }));
            }}
          />
          {errors.contacts && (
            <p className="text-[11px] text-red-600 mt-2 font-medium">{errors.contacts}</p>
          )}
        </div>

        {/* Bottom Action Buttons */}
        <div className="flex items-center justify-between pt-2 pb-12">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer shadow-2xs"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveAndExit}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer shadow-2xs"
            >
              <Bookmark className="w-4 h-4 text-slate-400" />
              <span>Save & exit</span>
            </button>

            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium shadow-xs transition-colors cursor-pointer"
            >
              <span>Save & continue</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Step2FacilitiesContacts;
